import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiUrl } from '../utils/apiBase';
import { cleanTournamentName } from '../utils/cleanTournamentName';

interface Category {
  id: number;
  name: string;
  discipline: string;
  maxPlayers: number | null;
  registrationCount: number;
}

interface TournamentDetail {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  registrationEndDate: string | null;
  venue: string;
  organizer: string;
  description: string | null;
  status: string;
  categories: Category[];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const DISCIPLINE_LABELS: Record<string, string> = {
  SINGLES_M: 'Singiel mężczyzn',
  SINGLES_F: 'Singiel kobiet',
  DOUBLES_M: 'Debel mężczyzn',
  DOUBLES_F: 'Debel kobiet',
  MIXED: 'Mikst',
  CUSTOM: 'Własna',
};

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tournament, setTournament] = useState<TournamentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }

    fetch(apiUrl(`/api/public/events/${id}`))
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((data: TournamentDetail) => {
        setTournament(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Nie udało się załadować turnieju.');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return <div className="event-detail-loading">Ładowanie...</div>;
  }

  if (error || !tournament) {
    return <div className="event-detail-error">{error ?? 'Nie znaleziono turnieju.'}</div>;
  }

  const isArchive =
    tournament.status === 'FINISHED' &&
    (tournament.organizer === 'Migracja archiwum' || !tournament.venue || tournament.venue === '—');

  return (
    <div className="event-detail">
      <Link to="/events" className="event-detail-back">
        ← Powrót do listy
      </Link>
      {isArchive && (
        <div className="event-detail-archive-banner">
          Turniej już rozegrany — to archiwalny zapis. Wyniki dostępne w sekcji{' '}
          <Link to="/tournaments">Wszystkie wyniki</Link>.
        </div>
      )}
      <h1 className="event-detail-name">{cleanTournamentName(tournament.name)}</h1>
      <div className={`event-detail-meta${isArchive ? ' event-detail-meta--archive' : ''}`}>
        <div>
          <strong>Termin:</strong>{' '}
          {tournament.startDate === tournament.endDate
            ? formatDate(tournament.startDate)
            : `${formatDate(tournament.startDate)} – ${formatDate(tournament.endDate)}`}
        </div>
        {tournament.venue && tournament.venue !== '—' && (
          <div>
            <strong>Miejsce:</strong> {tournament.venue}
          </div>
        )}
        {tournament.organizer && tournament.organizer !== 'Migracja archiwum' && (
          <div>
            <strong>Organizator:</strong> {tournament.organizer}
          </div>
        )}
        {tournament.registrationEndDate && (
          <div>
            <strong>Koniec zapisów:</strong> {formatDate(tournament.registrationEndDate)}
          </div>
        )}
      </div>
      {tournament.description && (
        <p className="event-detail-description">{tournament.description}</p>
      )}

      {!isArchive && (
        <>
          <h2>Kategorie</h2>
          {tournament.categories.length === 0 ? (
            <p>Brak kategorii.</p>
          ) : (
            <ul className="event-categories">
              {tournament.categories.map((cat) => (
                <li key={cat.id} className="event-category-card">
                  <h3 className="event-category-name">{cat.name}</h3>
                  <div className="event-category-discipline">
                    {DISCIPLINE_LABELS[cat.discipline] ?? cat.discipline}
                  </div>
                  <div className="event-category-count">
                    {cat.maxPlayers != null
                      ? `${cat.registrationCount} / ${cat.maxPlayers} uczestników`
                      : `${cat.registrationCount} uczestników`}
                  </div>
                  <div className="event-category-actions">
                    {tournament.status === 'REGISTRATION_OPEN' && (
                      <Link
                        to={`/events/${tournament.id}/register/${cat.id}`}
                        className="event-category-register"
                      >
                        Zapisz się
                      </Link>
                    )}
                    <Link
                      to={`/events/${tournament.id}/participants`}
                      className="event-category-participants"
                    >
                      Zobacz uczestników
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default EventDetail;
